import { useState } from "react";

interface FAQItem {
  question: string;
  answer: string;
}

interface ProductFAQProps {
  faqs: FAQItem[];
  productTitle: string;
}

export default function ProductFAQ({ faqs, productTitle }: ProductFAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-12 md:py-16 bg-background-100">
      <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
        {/* JSON-LD FAQPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.answer,
                },
              })),
            }),
          }}
        />
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
              FAQ
            </span>
            <h4 id="faq-section">
              <a href="#faq-section" className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 hover:text-primary-500 transition-colors">
                Common Questions About {productTitle}
              </a>
            </h4>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
              >
                <button
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  aria-expanded={openIndex === index}
                >
                  <span className="font-semibold text-foreground-900 text-sm md:text-base">
                    {faq.question}
                  </span>
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 flex-shrink-0">
                    <i
                      className={`text-foreground-700 text-base transition-transform duration-200 ${
                        openIndex === index ? "ri-subtract-line" : "ri-add-line"
                      }`}
                    ></i>
                  </span>
                </button>
                {openIndex === index && (
                  <div className="px-5 pb-5">
                    <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}